/**
 * Biological Age Component Test
 * Tests the PatientHealthOverview component with biological age data
 */

import React from 'react';
import PatientHealthOverview from '../components/PatientHealthOverview';
import { calculateLevinePhenoAge, calculateAdjustedBiologicalAge } from '../utils/riskCalculators';

// Test data with all required PhenoAge parameters
const testTableData = [
  {
    category: 'Labs',
    parameters: [
      { key: 'labs_protein_albumin', currentValue: 4.53, displayName: 'Albumin' },
      { key: 'labs_renal_creatinine', currentValue: 1.16, displayName: 'Creatinine' },
      { key: 'labs_glucose_glucose', currentValue: 78.2, displayName: 'Glucose' },
      { key: 'labs_inflammation_hscrp', currentValue: 5.93, displayName: 'C-Reactive Protein' },
      { key: 'labs_cbc_lymph_pct', currentValue: 30.9, displayName: 'Lymphocyte %' },
      { key: 'labs_cbc_mcv', currentValue: 83.5, displayName: 'Mean Cell Volume' },
      { key: 'labs_cbc_rdw', currentValue: 13.1, displayName: 'Red Cell Distribution Width' },
      { key: 'labs_hepatology_alp', currentValue: 46, displayName: 'Alkaline Phosphatase' },
      { key: 'labs_cbc_wbc', currentValue: 7.45, displayName: 'White Blood Cell Count' }
    ]
  }
];

const testPatientDemographics = {
  birthDate: '2000-01-01', // 24 years old
  age: 24
};

const testOverallDeviation = {
  average: 1.5,
  count: 50,
  sumOfAbsoluteZScores: 75
};

// Calculate biological age using the same logic as the hook
const calculateTestBiologicalAge = () => {
  const phenoAgeParams = {
    chronologicalAge: 24,
    labs_protein_albumin: 4.53,
    labs_renal_creatinine: 1.16,
    labs_glucose_glucose: 78.2,
    labs_inflammation_hscrp: 5.93,
    labs_cbc_lymph_pct: 30.9,
    labs_cbc_mcv: 83.5,
    labs_cbc_rdw: 13.1,
    labs_hepatology_alp: 46,
    labs_cbc_wbc: 7.45
  };

  const phenoAgeResult = calculateLevinePhenoAge(phenoAgeParams);
  
  if (phenoAgeResult.error) {
    return { error: phenoAgeResult.error };
  }

  const adjustmentParams = {
    phenoAge: phenoAgeResult.phenoAge,
    overallZScore: 1.5,
    phenoAgePanelZScore: 1.2,
    totalParametersWithData: 50,
    totalConfiguredParameters: 100
  };

  const adjustedAgeResult = calculateAdjustedBiologicalAge(adjustmentParams);
  
  return {
    phenoAge: phenoAgeResult,
    adjustedBiologicalAge: adjustedAgeResult,
    error: adjustedAgeResult.error
  };
};

const BiologicalAgeTest = () => {
  const biologicalAge = calculateTestBiologicalAge();

  console.log('🧬 Biological Age Test Data:', biologicalAge);

  return (
    <div style={{ padding: '20px' }}>
      <h2>Biological Age Component Test</h2>
      
      <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
        <h3>Test Data Summary:</h3>
        <p><strong>Patient Age:</strong> 24 years</p>
        <p><strong>Biological Age Status:</strong> {biologicalAge.error ? 'Error' : 'Success'}</p>
        {biologicalAge.error && <p><strong>Error:</strong> {biologicalAge.error}</p>}
        {!biologicalAge.error && (
          <>
            <p><strong>PhenoAge:</strong> {biologicalAge.phenoAge?.phenoAge?.toFixed(2)} years</p>
            <p><strong>Adjusted Biological Age:</strong> {biologicalAge.adjustedBiologicalAge?.adjustedBiologicalAge?.toFixed(2)} years</p>
          </>
        )}
      </div>

      <PatientHealthOverview
        loading={false}
        tableData={testTableData}
        overallDeviation={testOverallDeviation}
        patientDemographics={testPatientDemographics}
        biologicalAge={biologicalAge}
      />
    </div>
  );
};

export default BiologicalAgeTest;
